
import { useState, useMemo, useEffect } from "react";
import { ReactComponent as MagnifierIcon } from '../../../assets/icons/magnifier-icon.svg';
import { ReactComponent as CheckIcon } from '../../../assets/icons/check-icon.svg';
import { ReactComponent as CloseIcon } from '../../../assets/icons/x-icon.svg';
import { productOptionsService } from "../../../services/productFilterOptionsService";

// ─── Icons ────────────────────────────────────────────────────────────────────
const ChevronIcon = ({ open }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
    style={{ transition: "transform 0.25s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
    <path d="M4 6L8 10L12 6" stroke="#555" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ClearIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path d="M2 2L11 11M11 2L2 11" stroke="#aaa" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const SpinnerIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
    style={{ animation: "spin 0.7s linear infinite" }}>
    <circle cx="7" cy="7" r="5.5" stroke="#7CC805" strokeWidth="2" strokeDasharray="22 12" />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </svg>
);

// ─── Filter keys ──────────────────────────────────────────────────────────────
const FILTER_KEYS = ["brand", "category", "sub_category", "subline", "sku_code"];
const FILTER_LABELS = {
  brand: "Бренд",
  category: "Категория",
  sub_category: "Подкатегория",
  subline: "Сублинейка",
  sku_code: "Наименование",
};
const EMPTY = { brand: [], category: [], sub_category: [], subline: [], sku_code: [] };

// ─── Internal helpers ─────────────────────────────────────────────────────────
function HighlightMatch({ text, query }) {
  if (!query) return <span>{text}</span>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <span>{text}</span>;
  return (
    <span>
      {text.slice(0, idx)}
      <mark style={{ background: "#d4f56a", color: "#2a5000", borderRadius: 3, padding: "0 1px" }}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </span>
  );
}

function Checkbox({ checked, indeterminate, onChange, size = 18 }) {
  return (
    <span onClick={(e) => { e.stopPropagation(); onChange(!checked); }} style={{
      width: size, height: size, minWidth: size, borderRadius: 5, flexShrink: 0,
      border: `2px solid ${checked || indeterminate ? "#7CC805" : "#c5c5c5"}`,
      background: checked || indeterminate ? "#7CC805" : "#fff",
      display: "flex", alignItems: "center", justifyContent: "center",
      transition: "all 0.15s", boxShadow: checked || indeterminate ? "0 1px 4px #7CC80540" : "none",
      cursor: "pointer",
    }}>
      {indeterminate && !checked
        ? <span style={{ width: 8, height: 2, background: "white", borderRadius: 2, display: "block" }} />
        : checked ? <CheckIcon color="#fff" /> : null}
    </span>
  );
}

// ── AccordionGroup ────────────────────────────────────────────────────────────
function AccordionGroup({ label, items, committed, onApplyGroup, open, onToggle, searchQuery, globalLoading }) {
  const [draft, setDraft] = useState([...committed]);
  const [groupLoading, setGroupLoading] = useState(false);

  useEffect(() => { setDraft([...committed]); }, [committed]);

  const loading = globalLoading || groupLoading;
  const draftSet = new Set(draft);
  const committedSet = new Set(committed);
  const pendingAdd = draft.filter(v => !committedSet.has(v));
  const pendingRemove = committed.filter(v => !draftSet.has(v));
  const hasUnsaved = pendingAdd.length > 0 || pendingRemove.length > 0;

  const visibleItems = useMemo(() => {
    const mapped = items.map((item, oi) => ({ item, oi }));
    if (!searchQuery) return mapped;
    return mapped.filter(({ item }) => item.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [items, searchQuery]);

  const allChecked = visibleItems.length > 0 && visibleItems.every(({ item }) => draftSet.has(item));
  const anyChecked = visibleItems.some(({ item }) => draftSet.has(item));
  const indeterminate = anyChecked && !allChecked;

  const toggleItem = (value, checked) => {
    if (!checked) {
      setDraft(prev => prev.filter(v => v !== value));
    } else {
      setDraft(prev => [...prev, value]);
    }
  };

  const toggleAll = (visItems, val) => {
    if (!val) {
      setDraft(prev => prev.filter(v => !visItems.includes(v)));
    } else {
      setDraft(prev => [...new Set([...prev, ...visItems])]);
    }
  };

  const handleGroupApply = async (e) => {
    e.stopPropagation();
    setGroupLoading(true);
    try { await onApplyGroup(draft); }
    finally { setGroupLoading(false); }
  };

  const handleClear = async (e) => {
    e.stopPropagation();
    setDraft([]);
    setGroupLoading(true);
    try { await onApplyGroup([]); }
    finally { setGroupLoading(false); }
  };

  return (
    <div style={{
      borderRadius: 8,
      border: committed.length > 0 ? "1.5px solid #7CC805" : "1.5px solid #e7e7e7",
      background: "#fff",
      boxShadow: open ? "0 4px 16px #0000000d" : "0 1px 4px #0000000a",
      transition: "border-color 0.2s, box-shadow 0.2s",
      opacity: globalLoading ? 0.55 : 1,
    }}>
      <div onClick={onToggle} style={{
        display: "flex", alignItems: "center", gap: 10, padding: "13px 16px",
        background: open ? "#f7f7f8" : "#fff", cursor: "pointer", transition: "background 0.15s",
        borderBottom: open ? "1.5px solid #e7e7e7" : "none",
        position: "sticky", top: 0, zIndex: 2,
        borderRadius: open ? "8px 8px 0 0" : 8,
      }}>
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={allChecked} indeterminate={indeterminate}
            onChange={(val) => toggleAll(visibleItems.map(({ item }) => item), val)} size={18} />
        </span>
        <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: "#222", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          {label}
          {committed.length > 0 && (
            <span style={{ fontSize: 11.5, fontWeight: 600, color: "#d97706", background: "#fffbeb", borderRadius: 20, padding: "1px 8px" }}>
              {committed.length} применено
            </span>
          )}
          
          <div style={{ display: "flex", gap: 7, marginLeft: "auto", alignItems: "center" }}>
            {draft.length > 0 && (
              <button onClick={handleClear} disabled={loading} style={{
                padding: "6px 13px", borderRadius: 6, border: "1.5px solid #e7e7e7",
                background: "#fff", color: "#777", fontSize: 12.5, fontWeight: 500,
                cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.5 : 1,
              }}>Сбросить</button>
            )}
            {hasUnsaved && (
              <button onClick={handleGroupApply} disabled={loading} style={{
                padding: "6px 16px", borderRadius: 6, border: "none",
                background: loading ? "#ccc" : "#7CC805",
                color: "#fff", fontSize: 12.5, fontWeight: 600,
                cursor: loading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: 6,
                transition: "background 0.15s", boxShadow: "0 1px 6px #7CC80540",
              }}>
                {groupLoading && <SpinnerIcon color="white" />}
                Применить {draft.length}
              </button>
            )}
          </div>
        </span>
        {groupLoading ? <SpinnerIcon /> : <ChevronIcon open={open} />}
      </div>

      <div style={{
        maxHeight: open ? (visibleItems.length * 48 + 56) + "px" : "0px",
        overflow: "hidden", transition: "max-height 0.35s cubic-bezier(0.4,0,0.2,1)",
        borderRadius: "0 0 8px 8px",
      }}>
        {visibleItems.map(({ item, oi }, i) => {
          const isChecked = draftSet.has(item);
          const isPending = isChecked && !committedSet.has(item);
          return (
            <div key={oi} onClick={() => !loading && toggleItem(item, !isChecked)} style={{
              display: "flex", alignItems: "center", gap: 12, padding: "9px 16px 9px 44px",
              borderBottom: i < visibleItems.length - 1 ? "1px solid #f0f0f0" : "none",
              cursor: loading ? "not-allowed" : "pointer",
              background: isChecked ? (isPending ? "#fffef0" : "#f9ffe6") : "#fff",
              transition: "background 0.12s",
            }}>
              <Checkbox checked={isChecked} onChange={(val) => !loading && toggleItem(item, val)} size={16} />
              <span style={{ fontSize: 13.5, color: "#444", flex: 1 }}>
                <HighlightMatch text={item} query={searchQuery} />
              </span>
              {/* {isPending && <span style={{ fontSize: 11, color: "#d97706", fontWeight: 500 }}>применить</span>} */}
            </div>
          );
        })}

      </div>
    </div>
  );
}

// ── AccordionCheckboxSelector ─────────────────────────────────────────────────
//
//  Props:
//    filterOptions   { brand, category, sub_category, subline, sku_code }  — initial options (required)
//    filterBrands    your api.filterBrands(sku_code, brand, category, sub_category, subline)
//    onApply         (result: Record<string, string[]>) => void
//
//  Usage:
//    <AccordionCheckboxSelector
//      filterOptions={{ brand: ["Dorco"], category: ["Male"], sub_category: [], subline: [], sku_code: [] }}
//      filterBrands={api.filterBrands}
//      onApply={(result) => console.log(result)}
//    />                                                 selected items per group ([] if none).

export function AccordionCheckboxSelector({ filterOptions, selectedOptions, onApply, closeModal }) {
  const initialCommitted = useMemo(() => ({ ...EMPTY, ...(selectedOptions || {}) }), [selectedOptions]);

  const [options, setOptions]       = useState(filterOptions || EMPTY);
  const [committed, setCommitted]   = useState(initialCommitted);
  const [openGroups, setOpenGroups] = useState({ brand: false, category: false, sub_category: false, subline: false, sku_code: false });
  
  const [globalLoading, setGlobalLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [resetKey, setResetKey] = useState(0);

  // When filterOptions prop changes externally, update displayed options
  useEffect(() => {
    // if (filterOptions) setOptions(filterOptions);
    console.log("selectedOptions ", selectedOptions)
    fetchOptions(selectedOptions)
  }, [selectedOptions]);

  const fetchOptions  = async (currentSelected) => {
    setGlobalLoading(true);
    console.log("fetchOptions ", currentSelected)
    try {
      const data = await productOptionsService.filterBrands(currentSelected.sku_code, currentSelected.brand, currentSelected.category, currentSelected.sub_category, currentSelected.subline)      
      setOptions(data);
      setCommitted(prev => {
        const pruned = {};
        FILTER_KEYS.forEach(key => {
          const available = new Set(data[key] || []);
          pruned[key] = (prev[key] || []).filter(v => available.has(v));
        });
        return pruned;
      });
    } catch (e) {
      console.error("Failed to fetch filter options", e);
    } finally {
      setGlobalLoading(false);
    }
  };

  const handleGroupApply = async (key, values) => {
    const newCommitted = { ...committed, [key]: values };
    setCommitted(newCommitted);
    await fetchOptions(newCommitted);
  };

  const handleUnchecked = (key, values) => {
    setCommitted(prev => ({ ...prev, [key]: values }));
  };

  const handleReset = () => {
    setCommitted(EMPTY);
    setSearchQuery("");
    // if (filterOptions) setOptions(filterOptions);
    // else 
    setResetKey(k => k + 1);
    fetchOptions(EMPTY);
  };

  const totalCommitted = Object.values(committed).flat().length;

  const totalMatches = useMemo(() => {
    if (!searchQuery) return 0;
    return FILTER_KEYS.reduce((sum, key) =>
      sum + (options[key] || []).filter(i => i.toLowerCase().includes(searchQuery.toLowerCase())).length, 0);
  }, [options, searchQuery]);

  const hasNoMatches = searchQuery && totalMatches === 0;

  return (
    <div className="modal-overlay" onClick={() => closeModal()}>
        <div className="modal-container" style={{overflow: "hidden"}} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
                <h1>Продукты</h1>
                <button
                    onClick={() => closeModal()}
                    className='modal-header-close-button'
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#4b5563")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
                >
                    <CloseIcon size={24} />
                </button>
            </div>
            {/* Search */}
            <div style={{ position: "relative", marginTop: "20px", marginBottom: 6 }}>
              <span style={{
                position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
                display: "flex", alignItems: "center", pointerEvents: "none",
              }}>
                <MagnifierIcon width={16}/>
              </span>
              <input
                type="text" value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Поиск…"
                style={{
                  width: "100%", boxSizing: "border-box", padding: "10px 36px 10px 36px",
                  borderRadius: 8, border: "1.5px solid", fontFamily: "inherit", outline: "none",
                  borderColor: searchQuery ? "#7CC805" : "#e7e7e7",
                  background: "#fff", fontSize: 13.5, color: "#222",
                  boxShadow: searchQuery ? "0 0 0 3px #7CC80520" : "none",
                  transition: "border-color 0.15s, box-shadow 0.15s",
                }}
              />
              {searchQuery && (
                <span onClick={() => setSearchQuery("")} style={{
                  position: "absolute", right: 11, top: "50%", transform: "translateY(-50%)",
                  cursor: "pointer", display: "flex", alignItems: "center", padding: 3,
                }}>
                  <ClearIcon />
                </span>
              )}
            </div>

            {/* Search status */}
            {
              searchQuery && 
                <div style={{ height: 24, display: "flex", alignItems: "center", paddingLeft: 2, marginBottom: 8 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 500, color: hasNoMatches ? "#cc0044" : "#5a9900" }}>
                      {hasNoMatches
                        ? `Ничего не найдено для "${searchQuery}"`
                        : `Найдено: ${totalMatches} для "${searchQuery}"`
                      }
                    </span>
                </div>
            }

            {/* Groups */}
            <div style={{flex: 1, minHeight: 0, overflow: "auto"}}>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 14}}>
                {FILTER_KEYS.map(key => {
                  const items = options[key] || [];
                  if (searchQuery && !items.some(i => i.toLowerCase().includes(searchQuery.toLowerCase()))) return null;
                  return (
                    <AccordionGroup
                      key={key + resetKey}
                      label={FILTER_LABELS[key]}
                      items={items}
                      committed={committed[key]}
                      onApplyGroup={(values) => handleGroupApply(key, values)}
                      onUnchecked={(values) => handleUnchecked(key, values)}
                      open={openGroups[key]}
                      onToggle={() => setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }))}
                      searchQuery={searchQuery}
                      globalLoading={globalLoading}
                    />
                  );
                })}
              </div>

            </div>

            {/* Footer */}
            <div style={{ marginTop: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <span style={{ fontSize: 13, color: "#888" }}>
                {totalCommitted > 0
                  ? <><span style={{ color: "#7CC805", fontWeight: 600 }}>{totalCommitted}</span> {totalCommitted === 1 ? "фильтр применён" : totalCommitted < 5 ? "фильтра применено" : "фильтров применено"}</>
                  : "Фильтры не применены"}
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={handleReset} style={{
                  padding: "9px 18px", borderRadius: 8, border: "1.5px solid #e7e7e7",
                  background: "#fff", color: "#555", fontWeight: 500, fontSize: 13.5, cursor: "pointer",
                }}>Сбросить всё</button>
                
              <button
                onClick={() => {onApply?.(committed); closeModal();}}
                disabled={globalLoading}
                style={{
                  padding: "9px 22px", borderRadius: 8, border: "none",
                  background: "#7CC805", color: "#fff", fontWeight: 600, fontSize: 13.5,
                  cursor: globalLoading ? "not-allowed" : "pointer",
                  boxShadow: "0 2px 8px #7CC80540",
                  opacity: globalLoading ? 0.5 : 1,
                  transition: "opacity 0.15s",
              }}>
                Подтвердить выбор
              </button>
              </div>
            </div>
        </div>
    </div>
  );
}
